import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import FImage from 'components/BaseComponents/FImage'
import SBox from 'components/opinionless/SBox'
import SInputError from 'components/opinionless/SInputErrorV2'
import SInput from 'components/opinionless/SInputV2'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'
import { LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { HOUSEHOLD_MEMBER_RELATIONSHIP } from 'containers/Enrollment/HouseholdIncome/HouseholdIncomeMembersDOB'
import { useTranslation } from 'i18n'
import React, { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { StepWizardChildProps } from 'react-step-wizard'
import { SStyled, useSTheme } from 'styles/theme'
import { useBenefitsAnswersMutation } from 'types/graphql'
import { getAge } from 'utils/date'
import { ga4_useTitle } from 'utils/ga4'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import { useHouseholdMembers } from 'v2/hooks/useHouseholdMembers'
import style from './style.module.scss'

interface Props {
    setCurrentStep?: (number) => void
    previouslySelectedMembers: any
    applicationId: string
    personId: string
    fontFamily: any
    onNextStep?: any
    onPrevious?: any
    updatePercentage: any
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

const HouseholdIncomeEarnedUnearned: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)

    const applicationContext = useContext(ApplicationContext)

    const {
        loading: householdMembersLoading,
        error: householdMembersError,
        refetch: householdMembersRefetch,
    } = useHouseholdMembers()
    useApplicationBasicInfo()

    useEffect(() => {
        householdMembersRefetch()
    }, [householdMembersRefetch])
    useApplicationBasicInfo()

    const { register, handleSubmit, errors, setError, getValues } = useForm()

    ga4_useTitle('Household Income Earned Unearned')
    const requiredString = 'required_field'

    const fontFamily = props.fontFamily

    const [loading, setLoading] = useState(true)

    const [householdMembersDetails, setHouseholdMembersDetails] = useState([])
    const [saveBenefitAnswer, { loading: saveAnswersLoading }] = useBenefitsAnswersMutation()

    const [earnedIncomeList, setEarnedIncomeList] = useState([])
    const [unearnedIncomeList, setUnearnedIncomeList] = useState([])

    useEffect(() => {
        if (!householdMembersLoading) {
            setLoading(false)
            setHouseholdMembersDetails(
                applicationContext.householdMembers.map((member) => ({
                    id: member.id,
                    name: member.name,
                    relation: member.relation,
                    dob: member.dob,
                    blindDisabled: member.blindDisabled,
                    earnedIncome: member.earnedIncome,
                    unearnedIncome: member.unearnedIncome,
                }))
            )

            const formValues = getValues()

            const initialEarnedIncomeList = [
                {
                    householdMemberId: null,
                    personId: applicationContext.profile.person.id,
                    category: 'EARNED_INCOME',
                    answer: formValues.applicantEarnedIncome || applicationContext.profile.earnedIncome || 0,
                },
                ...applicationContext.householdMembers.map((member) => ({
                    householdMemberId: member.id,
                    personId: applicationContext.profile.person.id,
                    category: 'EARNED_INCOME',
                    answer: formValues[`memberEarnedIncome_${member.id}`] || member.earnedIncome || 0,
                })),
            ]

            const initialUnearnedIncomeList = [
                {
                    householdMemberId: null,
                    personId: applicationContext.profile.person.id,
                    category: 'UNEARNED_INCOME',
                    answer: formValues.applicantUnearnedIncome || applicationContext.profile.unearnedIncome || 0,
                },
                ...applicationContext.householdMembers.map((member) => ({
                    householdMemberId: member.id,
                    personId: applicationContext.profile.person.id,
                    category: 'UNEARNED_INCOME',
                    answer: formValues[`memberUnearnedIncome_${member.id}`] || member.unearnedIncome || 0,
                })),
            ]

            setEarnedIncomeList(initialEarnedIncomeList)
            setUnearnedIncomeList(initialUnearnedIncomeList)
        }
    }, [applicationContext.householdMembers])

    const selectedMembers = householdMembersDetails.filter((member) =>
        props.previouslySelectedMembers?.membersWithIncome.includes(member.id)
    )

    const handleIncomeChange = (memberId, category) => (e) => {
        const value = e.target.value

        const listToUpdate = category === 'EARNED_INCOME' ? setEarnedIncomeList : setUnearnedIncomeList

        listToUpdate((prevList) => {
            const updatedList = [...prevList]
            const memberIndex = updatedList.findIndex((item) => item.householdMemberId === memberId)

            if (memberIndex !== -1) {
                updatedList[memberIndex].answer = value
            } else {
                updatedList.push({
                    householdMemberId: memberId,
                    personId: applicationContext.profile.person.id,
                    category: category,
                    answer: value,
                })
            }

            return updatedList
        })
    }

    const onSubmit = () => {
        saveBenefitAnswer({
            variables: {
                answers: earnedIncomeList.concat(unearnedIncomeList),
            },
        }).then((response) => {
            if (response.data.addBenefitAnswers.ok) {
                props.nextStep()
            } else {
                setError('lbHouseholdIncomeEarnedUnearned', { type: 'oops', message: t('error') })
            }
        })
    }

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.householdInfo,
        WIZARD_STEPS_MAPPING.householdInfo.incomeValue,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    if (loading) {
        return null
    }

    return (
        <Container>
            <SBox as="form" onSubmit={handleSubmit(onSubmit)}>
                <FButtonPrevious onClick={props.previousStep} />
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '800',
                        lineHeight: '140%',
                        color: '#202020',
                        fontSize: '16px',
                        marginBottom: '24px',
                    }}
                    dangerouslySetInnerHTML={{
                        __html: t('long_beach_household_income_earned_unearned_subtitle'),
                    }}
                />
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '400',
                        lineHeight: '140%',
                        color: theme.colors.gray400,
                        fontSize: '14px',
                        marginBottom: '24px',
                    }}
                    dangerouslySetInnerHTML={{
                        __html: t('long_beach_household_income_earned_unearned_description'),
                    }}
                />
                {props.previouslySelectedMembers.applicantSelected && (
                    <div className={style.household_applicant_income_earned_unearned_container}>
                        <div className={style.household_applicant_income_earned_unearned_container__icon}>
                            <FImage imageUrl={'/lb_household_income_person.svg'} alt="person" />
                        </div>
                        <div className={style.household_applicant_income_earned_unearned_container__applicant_group}>
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '700',
                                    lineHeight: '140%',
                                    color: theme.colors.lbBlue,
                                    fontSize: '14px',
                                    marginBottom: '5px',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t('long_beach_applicant_name_label_relationship', {
                                        name: `${applicationContext?.profile?.firstName} ${applicationContext?.profile?.lastName}`,
                                        relationship: t('long_beach_household_applicant_label'),
                                    }),
                                }}
                            />
                            <SInput
                                name="applicantEarnedIncome"
                                type="number"
                                id="applicantEarnedIncome"
                                placeholder={t('long_beach_household_income_earned_income_input_placeholder')}
                                defaultValue={
                                    parseInt(applicationContext?.profile?.earnedIncome) !== 0
                                        ? applicationContext?.profile?.earnedIncome
                                        : null
                                }
                                onChange={handleIncomeChange(null, 'EARNED_INCOME')}
                                sx={{
                                    fontFamily: fontFamily.body,
                                    borderRadius: '8px',
                                    marginBottom: '5px',
                                }}
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 'normal',
                                    lineHeight: '20px',
                                    color: theme.colors.gray400,
                                }}
                                ref={register({
                                    required: requiredString,
                                    validate: {
                                        positiveNumber: (value) =>
                                            parseInt(value) >= 0 || 'positive_integer_validation',
                                    },
                                })}
                            />
                            <SInput
                                name="applicantUnearnedIncome"
                                type="number"
                                id="applicantUnearnedIncome"
                                placeholder={t('long_beach_household_income_unearned_income_input_placeholder')}
                                defaultValue={
                                    parseInt(applicationContext?.profile?.unearnedIncome) !== 0
                                        ? applicationContext?.profile?.unearnedIncome
                                        : null
                                }
                                onChange={handleIncomeChange(null, 'UNEARNED_INCOME')}
                                sx={{
                                    fontFamily: fontFamily.body,
                                    borderRadius: '8px',
                                    marginBottom: '5px',
                                }}
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 'normal',
                                    lineHeight: '20px',
                                    color: theme.colors.gray400,
                                }}
                                ref={register({
                                    required: requiredString,
                                    validate: {
                                        positiveNumber: (value) =>
                                            parseInt(value) >= 0 || 'positive_integer_validation',
                                    },
                                })}
                            />
                            {(errors[`applicantEarnedIncome`] || errors[`applicantUnearnedIncome`]) && (
                                <SInputError
                                    forElementId={`applicantUnearnedIncome`}
                                    sx={{
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.redError,
                                    }}
                                >
                                    {t(errors[`applicantEarnedIncome`]?.message) ||
                                        t(errors[`applicantUnearnedIncome`]?.message)}
                                </SInputError>
                            )}
                        </div>
                    </div>
                )}
                {selectedMembers?.map((member, index) => (
                    <div className={style.household_member_income_earned_unearned_container}>
                        <div className={style.household_member_income_earned_unearned_container__icon}>
                            <FImage imageUrl={'/lb_household_income_person_bw.svg'} alt="person" />
                        </div>
                        <div className={style.household_member_income_earned_unearned_container__member_group}>
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '700',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '5px',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t('long_beach_member_name_label_relationship_age', {
                                        name: `${member.name}`,
                                        relationship: t(`${HOUSEHOLD_MEMBER_RELATIONSHIP[member.relation]}`),
                                        age: `${getAge(member.dob)}`,
                                    }),
                                }}
                            />
                            <SInput
                                name={`memberEarnedIncome_${member.id}`}
                                type="number"
                                id={`memberEarnedIncome_${member.id}`}
                                placeholder={t('long_beach_household_income_earned_income_input_placeholder')}
                                defaultValue={parseInt(member?.earnedIncome) !== 0 ? member?.earnedIncome : null}
                                onChange={handleIncomeChange(member.id, 'EARNED_INCOME')}
                                sx={{
                                    fontFamily: fontFamily.body,
                                    borderRadius: '8px',
                                    marginBottom: '5px',
                                }}
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 'normal',
                                    lineHeight: '20px',
                                    color: theme.colors.gray400,
                                }}
                                ref={register({
                                    required: requiredString,
                                    validate: {
                                        positiveNumber: (value) =>
                                            parseInt(value) >= 0 || 'positive_integer_validation',
                                    },
                                })}
                            />
                            <SInput
                                name={`memberUnearnedIncome_${member.id}`}
                                type="number"
                                id={`memberUnearnedIncome_${member.id}`}
                                placeholder={t('long_beach_household_income_unearned_income_input_placeholder')}
                                defaultValue={parseInt(member?.unearnedIncome) !== 0 ? member?.unearnedIncome : null}
                                onChange={handleIncomeChange(member.id, 'UNEARNED_INCOME')}
                                sx={{
                                    fontFamily: fontFamily.body,
                                    borderRadius: '8px',
                                    marginBottom: '5px',
                                }}
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 'normal',
                                    lineHeight: '20px',
                                    color: theme.colors.gray400,
                                }}
                                ref={register({
                                    required: requiredString,
                                    validate: {
                                        positiveNumber: (value) =>
                                            parseInt(value) >= 0 || 'positive_integer_validation',
                                    },
                                })}
                            />
                            {(errors[`memberEarnedIncome_${member.id}`] ||
                                errors[`memberUnearnedIncome_${member.id}`]) && (
                                <SInputError
                                    forElementId={`memberUnearnedIncome_${member.id}`}
                                    sx={{
                                        fontFamily: fontFamily.body,
                                        color: theme.colors.redError,
                                    }}
                                >
                                    {t(errors[`memberEarnedIncome_${member.id}`]?.message) ||
                                        t(errors[`memberUnearnedIncome_${member.id}`]?.message)}
                                </SInputError>
                            )}
                        </div>
                    </div>
                ))}
                <LBButton
                    type={'submit'}
                    id={'continueButton'}
                    loading={saveAnswersLoading}
                    title={t('save_and_continue')}
                    width={'100%'}
                    mt={7}
                    mb={5}
                    textStyle={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '140%',
                        fontFamily: fontFamily.body,
                        color: theme.colors.white,
                    }}
                >
                    {t('save_and_continue')}
                </LBButton>
                {errors.lbHouseholdIncomeEarnedUnearned && (
                    <SInputError
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                        forElementId={'continueButton'}
                    >
                        {t(errors.lbHouseholdIncomeEarnedUnearned?.message)}
                    </SInputError>
                )}
            </SBox>
        </Container>
    )
}

export default HouseholdIncomeEarnedUnearned
